/**
 * The measuring aid for a live: with `?stats=1` on the page's address, every tile carries a small line of what
 * its picture really is (size, frames a second, kilobits a second), and your own tile what you send, one line
 * per size. It reads the page's peer connections, which it collects by wrapping the browser's constructor, so
 * it has to load before the meeting joins. With the address plain, nothing is wrapped, nothing is read and no
 * tile draws a line.
 */

/** Whether this page was opened with `?stats=1`. Read once: the address does not change inside the live. */
export const STATS_ON = typeof location !== "undefined" && new URLSearchParams(location.search).get("stats") === "1";

/** What one picture is right now. */
export interface PictureStats {
  /** The size's name where the sender gave it (f, h, q); empty for a picture sent as one size. */
  rid: string;
  width: number;
  height: number;
  fps: number;
  kbps: number;
}

interface Counters {
  at: number;
  bytes: number;
}

/** What one reading of the connections found, by the id of the track each picture belongs to. */
export interface Readings {
  received: Map<string, PictureStats>;
  sent: Map<string, PictureStats[]>;
}

type Stat = Record<string, unknown>;
const text = (v: unknown): string => (typeof v === "string" ? v : "");
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/**
 * One connection's report read into pictures. `before` holds the byte counts of the reading before, by the
 * report's own id, so a rate can be worked out; it is updated for the next one.
 */
export function readReport(report: Iterable<Stat>, before: Map<string, Counters>, into: Readings): void {
  const all = [...report];
  const sources = new Map<string, string>();
  for (const s of all) if (s.type === "media-source" && s.kind === "video") sources.set(text(s.id), text(s.trackIdentifier));
  for (const s of all) {
    if (s.kind !== "video" && s.mediaType !== "video") continue;
    const inbound = s.type === "inbound-rtp";
    if (!inbound && s.type !== "outbound-rtp") continue;
    const id = text(s.id);
    const at = num(s.timestamp);
    const bytes = num(inbound ? s.bytesReceived : s.bytesSent);
    const was = before.get(id);
    before.set(id, { at, bytes });
    const seconds = was ? (at - was.at) / 1000 : 0;
    const picture: PictureStats = {
      rid: text(s.rid),
      width: num(s.frameWidth),
      height: num(s.frameHeight),
      fps: Math.round(num(s.framesPerSecond)),
      kbps: seconds > 0 ? Math.round(((bytes - (was?.bytes ?? 0)) * 8) / seconds / 1000) : 0,
    };
    if (inbound) {
      const track = text(s.trackIdentifier);
      if (track) into.received.set(track, picture);
    } else {
      const track = sources.get(text(s.mediaSourceId));
      if (!track) continue;
      into.sent.set(track, [...(into.sent.get(track) ?? []), picture]);
    }
  }
}

/** One picture as a line: "f 1920x1080 30 fps 2510 kbps". */
export function pictureLine(p: PictureStats): string {
  const size = p.width > 0 ? `${p.width}x${p.height}` : "no picture";
  return `${p.rid ? `${p.rid} ` : ""}${size} ${p.fps} fps ${p.kbps} kbps`;
}

// --- the connections, collected as the page makes them -------------------------------------------------
const connections = new Set<RTCPeerConnection>();
const counters = new Map<RTCPeerConnection, Map<string, Counters>>();
let latest: Readings = { received: new Map(), sent: new Map() };
const listeners = new Set<() => void>();
let timer: number | undefined;

if (STATS_ON && typeof RTCPeerConnection !== "undefined") {
  const Native = RTCPeerConnection;
  window.RTCPeerConnection = class extends Native {
    constructor(...args: ConstructorParameters<typeof RTCPeerConnection>) {
      super(...args);
      connections.add(this);
    }
  };
}

async function sample(): Promise<void> {
  const next: Readings = { received: new Map(), sent: new Map() };
  for (const pc of connections) {
    if (pc.connectionState === "closed") {
      connections.delete(pc);
      counters.delete(pc);
      continue;
    }
    let before = counters.get(pc);
    if (!before) counters.set(pc, (before = new Map()));
    try {
      readReport((await pc.getStats()).values() as Iterable<Stat>, before, next);
    } catch {
      // a connection that closed while it was being read has nothing to say
    }
  }
  latest = next;
  for (const l of listeners) l();
}

/** Called with each new reading, once a second while anyone listens. */
export function watchStats(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === undefined) {
    void sample();
    timer = window.setInterval(() => void sample(), 1000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== undefined) {
      window.clearInterval(timer);
      timer = undefined;
    }
  };
}

/**
 * What the last reading found for this track: a received picture, or the sizes sent. A sent camera that
 * the page's own track id does not name (the kit may send a copy of it) is taken to be the video source
 * sent in the most sizes, which is the camera and never a screen.
 */
export function statsOf(trackId: string, mine: boolean): string[] {
  if (mine) {
    const sent = latest.sent.get(trackId) ?? [...latest.sent.values()].toSorted((a, b) => b.length - a.length)[0] ?? [];
    return sent.toSorted((a, b) => b.width - a.width).map(pictureLine);
  }
  const got = latest.received.get(trackId);
  return got ? [pictureLine(got)] : [];
}
